<script lang="ts">
import PlanCard from '@/components/cards/PlanCard.vue'
import { usePlansStore } from '@/stores/plans'
import type { Plan, SwitchOptions } from '@/types'
import {
  MembershipPeriods,
  NamedPlanTiers,
  type TMembershipPeriodValue,
  Themes,
} from '@/utils/helper-objects'
import { defineComponent, useTemplateRef } from 'vue'

import PlanPostalCard from '../cards/PlanPostalCard.vue'
import type AppCarousel from '../carousel/AppCarousel.vue'
import CarouselItem from '../carousel/CarouselItem.vue'

/**
 * Plans Section.
 */
export default defineComponent({
  components: {
    PlanCard,
    PlanPostalCard,
    CarouselItem,
  },
  setup() {
    const plansStore = usePlansStore()

    return {
      plansStore,
      NamedPlanTiers,
      Themes,
      carouselRef: useTemplateRef<InstanceType<typeof AppCarousel>>('carousel'),
    }
  },
  data() {
    return {
      membershipPeriods: [
        {
          value: MembershipPeriods.Monthly,
          label: this.$t('date.monthly'),
        },
        {
          value: MembershipPeriods.Yearly,
          label: this.$t('date.annual'),
        },
      ] as Array<SwitchOptions>,
    }
  },
  computed: {
    plans() {
      return this.plansStore.plansArray.filter(
        (plan) =>
          (plan.period === this.$plansStore.selectedMembershipPeriod &&
            plan.tier != NamedPlanTiers.Platinum) ||
          plan.tier === NamedPlanTiers.Postal
      )
    },
  },
  watch: {
    '$plansStore.selectedMembershipPeriod'(period: TMembershipPeriodValue) {
      let planIndex = 0

      if (period === MembershipPeriods.Yearly) {
        planIndex =
          this.plans.findIndex(
            (plan) =>
              plan.tier === NamedPlanTiers.Standard && plan.period === period
          ) ?? 0
      } else {
        planIndex =
          this.plans.findIndex(
            (plan) => plan.tier === NamedPlanTiers.Vip && plan.period === period
          ) ?? 0
      }

      void this.$nextTick(() => {
        this.carouselRef?.scrollbox?.scrollTo(planIndex)
      })
    },
  },
  async mounted() {
    await this.plansStore.fetchPlans()
  },
})
</script>

<template>
  <AppSection :class="{ hidden: $userStore.isLoggedIn }" class="plans-section">
    <AppCarousel
      ref="carousel"
      title="Our Plans"
      :icon="Icons.StarRefresh"
      :view-all-route="{ name: 'memberships' }"
      :autoplay="false"
      :autoplay-delay="0"
    >
      <template #preamble>
        <AppSwitch
          v-model="$plansStore.selectedMembershipPeriod"
          :options="membershipPeriods"
          name="membership-period-options"
          :theme="Themes.Light"
          class="mx-auto w-full my-2 max-w-screen-sm !p-2 !leading-5"
        />
      </template>

      <CarouselItem
        v-for="plan in plans"
        :key="plan.code"
        class="!flex !flex-1 min-w-[280px] !px-0"
      >
        <PlanPostalCard
          v-if="plan.tier === NamedPlanTiers.Postal"
          class="mx-2"
        />
        <PlanCard
          v-else
          :plan="plan"
          :selected-membership-period="
            plansStore.selectedMembershipPeriod as TMembershipPeriodValue
          "
          class="mx-2"
        />
      </CarouselItem>
    </AppCarousel>
  </AppSection>
</template>

<style lang="scss" scoped>
.plans-section:deep(.scrollbox > ul) {
  @apply mb-0 pb-4;
}
</style>
